/**
 * D-020 LT2-005 — are Polymarket-only lines hidden from the execution view?
 *
 * One answer, one place, and its own module rather than the board's: the
 * dashboard has to ask the same question when it builds a discovery catalog of
 * spread/total events (#754), and importing it from `SpreadsTotals/index.tsx`
 * would pull the whole board in behind it — which the dashboard's own test
 * files replace with a stub, so the shared rule would vanish with it.
 */
import { filterEventExecutableLines } from './synthesize';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useLt2Access } from '@/hooks/useLt2Access';
import type { GameEventRow } from '../ValueDashboard/types';

/**
 * The rule itself: under lt2, Poly-only lines are hidden unless the reveal
 * toggle is on — and the nearest-three strip forces them hidden regardless.
 *
 * Two copies of this condition would be two things to keep in step, and the
 * consequence of them disagreeing is a scope line that counts an event the
 * board then declines to draw.
 */
export function useHidePolyOnlyLines(): boolean {
  const lt2Access = useLt2Access();
  const showPolyOnlyLines = useDisplaySettings((s) => s.spreadTotalShowPolyOnlyLines);
  const nearestLinesOn = useDisplaySettings((s) => s.spreadTotalNearestLines);
  return lt2Access && (!showPolyOnlyLines || nearestLinesOn);
}

/**
 * The rule applied: hidden lines removed, and events left with no line at all
 * removed with them.
 *
 * Returns the rows UNTOUCHED when nothing is hidden — the same reference, so a
 * caller memoising on identity does not churn for a filter that did nothing.
 */
export function executableEvents(
  rows: readonly GameEventRow[],
  hide: boolean,
): readonly GameEventRow[] {
  if (!hide) return rows;
  return rows
    .map((r) => filterEventExecutableLines(r, false))
    .filter((r) => r.spreads.length + r.totals.length > 0);
}
